import { useRef, useMemo, memo } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { User } from '@/types/database';
import { UserCard } from '@/components/UserCard';
import { GRIDS } from '@/design-system';

interface VirtualizedUserGridProps {
  users: User[];
  columns?: number;
  rowHeight?: number;
  gap?: number;
  /** Map of user ID to localized bio (metadata-first pattern) */
  localizedBiosMap?: Map<string, string | undefined>;
}

const VirtualizedUserGridInner = ({ 
  users, 
  columns = 3,
  rowHeight = 200,
  gap = 16,
  localizedBiosMap
}: VirtualizedUserGridProps) => {
  const listRef = useRef<HTMLDivElement>(null);

  // Group users into rows
  const rows = useMemo(() => {
    const result: User[][] = [];
    for (let i = 0; i < users.length; i += columns) {
      result.push(users.slice(i, i + columns));
    }
    return result;
  }, [users, columns]);

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => rowHeight + gap,
    overscan: 3,
    scrollMargin: listRef.current?.offsetTop ?? 0,
  });

  const virtualRows = virtualizer.getVirtualItems();

  return (
    <div ref={listRef} style={{ width: '100%' }}>
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualRows.map((virtualRow) => {
          const row = rows[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
              }}
            >
              <div className={GRIDS.featuredGrid}>
                {row.map((user) => (
                  <UserCard 
                    key={user.id} 
                    expert={user} 
                    localizedBio={localizedBiosMap?.get(user.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const VirtualizedUserGrid = memo(VirtualizedUserGridInner);
